import React, { useState } from 'react'
import axios from 'axios'
import { useDispatch } from 'react-redux'
import { Button, Modal, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { fetchAllPublicKeys } from '../integrationsSlice'
import { fetchAllSecrets } from '../../../features/aws/awsSecretsManagerSlice'
import { getAxiosDefaultConfig } from '../../../modules/utils'
import { SM_INTEGRATION_UNINSTALL_URLS } from '../smUninstallEndpoints'

type Props = {
  visible: boolean
  closeModal: () => void
  channelId: string
  channelTitle: string
  connectionId: string
  ownerId: string | number | null | undefined
}

export default function SmConnectionUninstallModal({
  visible,
  closeModal,
  channelId,
  channelTitle,
  connectionId,
  ownerId,
}: Props) {
  const dispatch = useDispatch()
  const [isProcessing, setIsProcessing] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [showError, setShowError] = useState(false)

  const buildUrl = SM_INTEGRATION_UNINSTALL_URLS[channelId]

  const handleConfirm = async () => {
    if (!buildUrl) {
      toast.error('Disconnect is not configured for this integration.')
      return
    }
    setIsProcessing(true)
    setShowError(false)
    try {
      await axios.delete(buildUrl(connectionId), getAxiosDefaultConfig())
      if (ownerId != null && String(ownerId).trim() !== '') {
        await dispatch(fetchAllPublicKeys({ ownerId })).unwrap()
        await dispatch(fetchAllSecrets({ ownerId })).unwrap()
      }
      setCompleted(true)
      toast.success(`${channelTitle} connection disconnected`)
    } catch (err: any) {
      console.error('Error disconnecting SM integration:', err)
      setShowError(true)
      toast.error(err?.response?.data?.error || 'Failed to disconnect. Please try again.')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleClose = () => {
    closeModal()
    setCompleted(false)
    setShowError(false)
  }

  return (
    <Modal show={visible} onHide={handleClose}>
      <Modal.Body>
        <div className="tw-p-6">
          <h2 className="tw-text-xl tw-mb-6">Disconnect {channelTitle}</h2>
          {showError && (
            <p className="tw-text-red-600 tw-mb-4">Error disconnecting. Please try again.</p>
          )}
          <p className="tw-mb-4">
            {completed
              ? 'This connection has been disconnected.'
              : channelId === 'makeswift'
                ? `Remove this ${channelTitle} connection? PayHelm will delete your stored API key. You can reconnect anytime.`
                : `Remove this ${channelTitle} connection? PayHelm will delete stored OAuth credentials. You can reconnect anytime.`}
          </p>
          <div className="tw-flex tw-justify-end tw-space-x-2">
            <Button variant="secondary" size="sm" onClick={handleClose}>
              {completed ? 'Done' : 'Cancel'}
            </Button>
            {!completed && (
              <Button variant="danger" size="sm" onClick={handleConfirm} disabled={isProcessing || !buildUrl}>
                {isProcessing ? (
                  <>
                    <Spinner animation="border" size="sm" className="tw-mr-2" />
                    Disconnecting...
                  </>
                ) : (
                  'Disconnect'
                )}
              </Button>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}
